// dynamic-route.service.ts
import { Injectable, OnDestroy } from '@angular/core';
import { Router, Routes } from '@angular/router';
import { ContentService } from './content.service';
import { content } from 'src/environments/data';
import { BehaviorSubject, of, catchError, Subject, takeUntil, firstValueFrom, filter } from 'rxjs';
import { scheduleIdleTask } from '../shared/utils/schedule-idle';

/** Nav item shape consumed by HeaderComponent. */
export interface InfoRouteNavItem {
  id: string;
  originalId: string;
  path: string;
  title: string;
  data: { meta: string };
}

@Injectable({
  providedIn: 'root'
})
export class DynamicRouteService implements OnDestroy {
  private isLoadingRoutes = false;
  private routesLoaded = false;
  private hasInitialized = false;
  private destroy$ = new Subject<void>();

  /** Header nav — populated when info routes load (single fetch, no duplicate API). */
  readonly infoRoutesNav$ = new BehaviorSubject<InfoRouteNavItem[]>([]);

  constructor(
    private router: Router,
    private contentService: ContentService
  ) {
    this.contentService.contentChanges$
      .pipe(
        filter((event) => ContentService.affectsInfoRoutes(event)),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        void this.refreshInfoRoutes();
      });
  }

  /** Defer CMS route fetch on homepage cold load to shorten main-thread work. */
  scheduleInitializeWhenIdle(timeoutMs = 8000): void {
    if (this.routesLoaded || this.isLoadingRoutes || this.hasInitialized) {
      return;
    }
    scheduleIdleTask(() => void this.initializeRoutes(), timeoutMs);
  }

  private initPromise: Promise<void> | null = null;

  initializeRoutes(): Promise<void> {
    console.log('🚀 DynamicRouteService: Starting route initialization...');

    if (this.routesLoaded) {
      console.log('✅ Routes already loaded - skipping');
      return Promise.resolve();
    }
    if (this.initPromise) {
      console.log('⏳ Route initialization already in-flight - sharing promise');
      return this.initPromise;
    }

    this.hasInitialized = true;
    this.isLoadingRoutes = true;

    this.initPromise = this.fetchAndApplyRoutes().finally(() => {
      this.initPromise = null;
    });

    return this.initPromise;
  }

  /** Re-fetch published info routes after CMS publish/unpublish/delete/slug change. */
  refreshInfoRoutes(): Promise<void> {
    if (this.isLoadingRoutes) {
      return Promise.resolve();
    }
    this.contentService.clearInfoRoutesCache();
    this.hasInitialized = false;
    this.routesLoaded = false;
    this.isLoadingRoutes = true;
    return this.fetchAndApplyRoutes();
  }

  private fetchAndApplyRoutes(): Promise<void> {
    return firstValueFrom(
      this.contentService.getAllInfoRoutes().pipe(
        takeUntil(this.destroy$),
        catchError(error => {
          console.error('❌ Error loading dynamic routes:', error);
          this.isLoadingRoutes = false;
          this.hasInitialized = false;
          return of([]);
        })
      )
    )
      .then((dynamicRoutes: any[]) => {
        console.log('📋 Fetched dynamic routes:', dynamicRoutes);
        this.infoRoutesNav$.next(this.mapInfoRoutesForNav(dynamicRoutes));
        this.updateRouterConfig(dynamicRoutes);
        if (Array.isArray(dynamicRoutes) && dynamicRoutes.length > 0) {
          this.routesLoaded = true;
        } else {
          this.routesLoaded = false;
        }
        this.isLoadingRoutes = false;
        this.hasInitialized = true;
        console.log('✅ Dynamic routes initialization complete');
      })
      .catch((error) => {
        console.error('❌ Error in route initialization:', error);
        this.isLoadingRoutes = false;
        this.hasInitialized = false;
      });
  }

  addDynamicRoutesFromBackend(): void {
    console.log('📞 Legacy method called - redirecting to initializeRoutes()');

    if (this.routesLoaded) {
      console.log('✅ Routes already loaded - skipping legacy call');
      return;
    }

    this.initializeRoutes().catch(error => {
      console.error('❌ Error in legacy route loading:', error);
    });
  }

  private mapInfoRoutesForNav(data: any[]): InfoRouteNavItem[] {
    const seenPaths = new Set<string>();
    return (data || []).map((route: any, index: number) => {
      const basePath = this.sanitizeRoutePath(String(route.routeSlug || route.title || '').toLowerCase());
      let uniquePath = basePath;
      let pathCounter = 0;
      while (seenPaths.has(uniquePath)) {
        pathCounter++;
        uniquePath = `${basePath}${pathCounter}`;
      }
      seenPaths.add(uniquePath);
      const uniqueId = `route-${index}-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
      return {
        id: uniqueId,
        originalId: route._id || route.id,
        path: uniquePath,
        title: route.title,
        data: { meta: content.WEBDDN_TITTLE + '- ' + route.title },
      };
    });
  }

  private updateRouterConfig(dynamicRoutes: any[]): void {
    console.log('🔧 Updating router configuration...');

    const currentRoutes = [...this.router.config];

    const filteredRoutes = currentRoutes.filter(route =>
      !route.data?.['isDynamic'] && route.path !== '**'
    );

    const newRoutes: Routes = [];
    const seenPaths = new Set<string>();

    for (const route of (dynamicRoutes || [])) {
      const pathsToAdd: string[] = [];
      if (route.routeSlug) {
        pathsToAdd.push(this.sanitizeRoutePath(route.routeSlug));
      }
      if (route.title) {
        pathsToAdd.push(this.sanitizeRoutePath(route.title));
      }

      for (const p of pathsToAdd) {
        if (p && !seenPaths.has(p)) {
          seenPaths.add(p);
          newRoutes.push({
            path: p,
            loadComponent: () => import('../dynamic-page/dynamic-page.component')
              .then(m => m.DynamicPageComponent),
            data: {
              contentId: route._id || route.id,
              id: route._id || route.id,
              meta: content.WEBDDN_META,
              title: route.title,
              Title: route.pageTitle || route.title,
              isDynamic: true
            }
          });
        }
      }
    }

    console.log('➕ Adding dynamic routes:', newRoutes.length);

    const allRoutes = [
      ...filteredRoutes,
      ...newRoutes,
      {
        path: '**',
        loadComponent: () =>
          import('../home/home.component').then((m) => m.HomeComponent),
        data: {
          meta: content.WEBDDN_META,
          isNotFound: true
        }
      }
    ];

    this.router.resetConfig(allRoutes);

    console.log(`✅ Router configuration updated: ${this.router.config.length} total routes`);
    console.log(`📊 Routes breakdown: ${filteredRoutes.length} static + ${newRoutes.length} dynamic + 1 wildcard`);
  }

  areRoutesLoaded(): boolean {
    return this.routesLoaded;
  }

  isLoading(): boolean {
    return this.isLoadingRoutes;
  }

  reset(): void {
    console.log('🔄 Resetting DynamicRouteService state');
    this.isLoadingRoutes = false;
    this.routesLoaded = false;
    this.hasInitialized = false;
    this.initPromise = null;
    this.infoRoutesNav$.next([]);
  }

  private sanitizeRoutePath(path: string): string {
    if (!path) return '';

    return path
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^\w\-]/g, '')
      .replace(/\-\-+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  ngOnDestroy(): void {
    console.log('🧹 DynamicRouteService: Cleaning up');
    this.destroy$.next();
    this.destroy$.complete();
  }
}
